import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { flowFetch, FlowArea, FlowUser, FlowDocument, CATEGORY_LABELS, formatDate } from './flowApi';
import { MentionField, resolveMentions } from './MentionField';
import { AttachmentList } from './FlowAttachments';

// ---------- Editor / visualizador ----------

interface DocModalProps {
  docId?: string; // undefined = novo
  areas: FlowArea[];
  users: FlowUser[];
  defaultAreaId?: string;
  focusCommentId?: string; // deep-link: rola até este comentário e o destaca
  onClose: () => void;
  onSaved: () => void;
}

function DocModal({ docId, areas, users, defaultAreaId, focusCommentId, onClose, onSaved }: DocModalProps) {
  const { user } = useAuth();
  const [doc, setDoc] = useState<FlowDocument | null>(null);
  const [title, setTitle] = useState('');
  const [areaId, setAreaId] = useState(defaultAreaId || '');
  const [category, setCategory] = useState('GERAL');
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState('AREA');
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!docId);

  const canEdit = !docId || (doc && user && (doc.createdById === user.id || user.role !== 'USER'));

  const load = async () => {
    if (!docId) return;
    try {
      const data = await flowFetch(`/api/flow/documents/${docId}`);
      const d = data.document;
      setDoc(d);
      setTitle(d.title); setAreaId(d.areaId || ''); setCategory(d.category);
      setContent(d.content || ''); setVisibility(d.visibility);
      setSharedUserIds((d.shares || []).map((s: any) => s.userId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar documento');
      onClose();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [docId]);

  // Deep-link de notificação: rola até o comentário indicado assim que o documento carrega
  useEffect(() => {
    if (!focusCommentId || !doc) return;
    const timer = setTimeout(() => {
      document.getElementById(`flow-doc-comment-${focusCommentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommentId, doc?.id]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Informe o título'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ title, areaId: areaId || null, category, content, visibility, sharedUserIds });
      if (docId) await flowFetch(`/api/flow/documents/${docId}`, { method: 'PUT', body });
      else await flowFetch('/api/flow/documents', { method: 'POST', body });
      toast.success('Documento salvo!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const sendComment = async () => {
    if (!comment.trim() || !docId) return;
    const mentions = resolveMentions(comment, users);
    try {
      await flowFetch('/api/flow/comments', { method: 'POST', body: JSON.stringify({ documentId: docId, content: comment, mentions }) });
      setComment('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao comentar');
    }
  };

  const removeComment = async (commentId: string) => {
    try {
      await flowFetch(`/api/flow/comments/${commentId}`, { method: 'DELETE' });
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir comentário');
    }
  };
  const isAdmin = !!user && user.role !== 'USER';
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!docId || deleting) return;
    if (!window.confirm(`Excluir o documento "${title || doc?.title || ''}"? Esta ação não pode ser desfeita.`)) return;
    setDeleting(true);
    try {
      await flowFetch(`/api/flow/documents/${docId}`, { method: 'DELETE' });
      toast.success('Documento excluído!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir documento');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div></div>;
  }

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="mb-4 text-lg font-bold text-slate-900">{docId ? (canEdit ? 'Editar documento' : title) : 'Novo documento'}</h2>
        <div className="space-y-3">
          {canEdit ? (
            <>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Título do documento"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold" />
              <div className="grid grid-cols-2 gap-3">
                <select value={areaId} onChange={e => setAreaId(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Sem área</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <select value={category} onChange={e => setCategory(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <textarea value={content} onChange={e => setContent(e.target.value)} rows={10} placeholder="Conteúdo do documento..."
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={visibility === 'AREA'} onChange={e => setVisibility(e.target.checked ? 'AREA' : 'INVOLVED')} className="accent-cyan-600" />
                Visível para toda a área (desmarcado: somente pessoas liberadas)
              </label>
              <details className="text-sm text-slate-600">
                <summary className="cursor-pointer font-semibold">Liberar para pessoas específicas ({sharedUserIds.length})</summary>
                <div className="mt-2 grid max-h-40 grid-cols-2 gap-1 overflow-y-auto">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2">
                      <input type="checkbox" className="accent-cyan-600" checked={sharedUserIds.includes(u.id)}
                        onChange={e => setSharedUserIds(e.target.checked ? [...sharedUserIds, u.id] : sharedUserIds.filter(id => id !== u.id))} />
                      {u.nome}
                    </label>
                  ))}
                </div>
              </details>
            </>
          ) : (
            <div className="whitespace-pre-wrap rounded-lg bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-800">{content || 'Documento vazio.'}</div>
          )}

          {docId && (
            <div className="border-t border-slate-200 pt-3">
              <AttachmentList
                attachments={(doc as any)?.attachments || []}
                uploadPath={`/api/flow/documents/${docId}/attachments`}
                onChanged={load}
                currentUserId={user?.id}
                isAdmin={isAdmin}
              />
            </div>
          )}

          {docId && (
            <div className="border-t border-slate-200 pt-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Comentários</p>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {(doc?.comments || []).map((c: any) => (
                  <div key={c.id} id={`flow-doc-comment-${c.id}`}
                    className={`group rounded-lg px-3 py-2 ${c.id === focusCommentId ? 'bg-cyan-50 ring-2 ring-cyan-300' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-600">{c.author?.nome || '—'} <span className="font-normal text-slate-400">· {formatDate(c.createdAt)}</span></p>
                      {isAdmin && (
                        <button onClick={() => removeComment(c.id)}
                          className="text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100" title="Excluir comentário">
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-800">{c.content}</p>
                  </div>
                ))}
                {(doc?.comments || []).length === 0 && <p className="text-sm text-slate-400">Nenhum comentário.</p>}
              </div>
              <div className="mt-2 flex gap-2">
                <MentionField value={comment} onChange={setComment} users={users} onSubmit={sendComment}
                  placeholder="Comentar... digite @ para mencionar" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button onClick={sendComment} className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700">Enviar</button>
              </div>
            </div>
          )}
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          {docId && isAdmin && (
            <button onClick={handleDelete} disabled={deleting}
              className="mr-auto rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
              {deleting ? 'Excluindo...' : 'Excluir documento'}
            </button>
          )}
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Fechar</button>
          {canEdit && (
            <button onClick={handleSave} disabled={saving}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Lista ----------

export function FlowDocumentList({ areas, users, fixedAreaId }: { areas: FlowArea[]; users: FlowUser[]; fixedAreaId?: string }) {
  const [docs, setDocs] = useState<FlowDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [editing, setEditing] = useState<{ id?: string } | null>(null);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const location = useLocation();

  // Deep-link de notificação: ?d=<docId>&c=<commentId> abre o documento (e rola até o comentário)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const d = params.get('d');
    if (d) {
      setEditing({ id: d });
      setFocusCommentId(params.get('c'));
      try { window.history.replaceState({}, document.title, location.pathname); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fixedAreaId) params.set('areaId', fixedAreaId);
      else if (areaFilter) params.set('areaId', areaFilter);
      if (catFilter) params.set('category', catFilter);
      if (search) params.set('search', search);
      const data = await flowFetch(`/api/flow/documents?${params}`);
      setDocs(data.documents);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [search, areaFilter, catFilter, fixedAreaId]);

  const areaName = (id?: string | null) => areas.find(a => a.id === id)?.name || '—';

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar documentos..."
          className="w-56 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        {!fixedAreaId && (
          <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">Por área</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Por categoria</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => setEditing({})}
          className="ml-auto rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-700">
          + Novo documento
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
      ) : docs.length === 0 ? (
        <p className="py-12 text-center text-slate-400">Nenhum documento encontrado.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {docs.map(d => (
            <button key={d.id} onClick={() => setEditing({ id: d.id })}
              className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="truncate font-bold text-slate-900">{d.title}</p>
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{CATEGORY_LABELS[d.category] || d.category}</span>
              </div>
              <p className="line-clamp-2 text-xs text-slate-500">{d.preview || 'Sem conteúdo'}</p>
              <p className="mt-2 text-[11px] text-slate-400">
                {areaName(d.areaId)} · {d.createdBy?.nome || '—'} · atualizado {formatDate(d.updatedAt)}
              </p>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <DocModal docId={editing.id} areas={areas} users={users} defaultAreaId={fixedAreaId}
          focusCommentId={focusCommentId || undefined}
          onClose={() => { setEditing(null); setFocusCommentId(null); }} onSaved={load} />
      )}
    </div>
  );
}
